import React, { useEffect, useState } from 'react';
import { SurveyData, User, UserRole, DynamicQuestion, SurveySection } from '../types';
import { getSurveys, getUsers, deleteSurvey, getDynamicQuestions, getDynamicSections } from '../services/storage';
import { Download, Search, Filter, Users, CheckCircle, Trophy, Loader, Trash2, Edit, Image as ImageIcon, Eye, X, Calendar, User as UserIcon } from 'lucide-react';
import SecureActionModal from './SecureActionModal';
import SurveyForm from './SurveyForm';
import { useToast } from './Toast';
// @ts-ignore
import JSZip from 'jszip';

interface DashboardProps {
  user: User;
}

const Dashboard: React.FC<DashboardProps> = ({ user }) => {
  const [data, setData] = useState<SurveyData[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [questions, setQuestions] = useState<DynamicQuestion[]>([]);
  const [sections, setSections] = useState<SurveySection[]>([]);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isZipping, setIsZipping] = useState(false);
  const [search, setSearch] = useState('');
  const [filterWard, setFilterWard] = useState('');
  
  // Edit State
  const [editingSurvey, setEditingSurvey] = useState<SurveyData | null>(null);

  // View Photo State
  const [viewPhotos, setViewPhotos] = useState<string[]>([]);
  const [viewPhotoIndex, setViewPhotoIndex] = useState(0);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState<string | null>(null);
  const { addToast } = useToast();

  const isSectionVisible = (secId: string) => {
    if (user.role === 'MASTER_ADMIN') return true;
    return (user.visibleSections || []).includes(secId);
  };

  // Function to refresh data
  const loadData = async () => {
    setLoading(true);
    try {
      const [surveysData, usersData, qs, secs] = await Promise.all([getSurveys(), getUsers(), getDynamicQuestions(), getDynamicSections()]);
      setData(surveysData);
      setUsers(usersData);
      setQuestions(qs);
      setSections(secs);
    } catch (error) {
      console.error("Failed to load dashboard data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleEditSuccess = () => {
    setEditingSurvey(null);
    addToast("Survey updated successfully", "success");
    loadData(); // Refresh list after edit
  };

  // HYBRID PHOTO FINDER (Explicit + Recursive)
  const getAllPhotosFromSurvey = (survey: SurveyData): string[] => {
      const uniquePhotos = new Set<string>();
      
      // 1. Explicit Check based on Question Definition (Most reliable)
      const imageQs = questions.filter(q => q.type === 'image');
      imageQs.forEach(q => {
          // Check nested in section
          if (survey[q.section] && survey[q.section][q.id]) {
              const val = survey[q.section][q.id];
              if (typeof val === 'string' && val.startsWith('data:image')) uniquePhotos.add(val);
          }
          // Check flat/root
          if (survey[q.id]) {
             const val = survey[q.id];
             if (typeof val === 'string' && val.startsWith('data:image')) uniquePhotos.add(val);
          }
      });

      // 2. Recursive Check (Catch-all for dynamic fields)
      const extract = (obj: any) => {
        if (!obj) return;
        if (typeof obj === 'string') {
            if (obj.startsWith('data:image')) uniquePhotos.add(obj);
            return;
        }
        if (Array.isArray(obj)) {
            obj.forEach(extract);
            return;
        }
        if (typeof obj === 'object') {
            // Avoid recursing into huge non-data objects if any
            Object.values(obj).forEach(extract);
        }
      };
      
      // Only extract from data sections to save performance
      sections.forEach(sec => {
          if (survey[sec.id]) extract(survey[sec.id]);
      });
      
      return Array.from(uniquePhotos);
  };

  // Stats Logic
  const totalFilled = data.length;
  
  const repStats = users.reduce((acc, user) => {
    if (user.role === 'REPRESENTATIVE' || user.role === 'ADMIN' || user.role === 'MASTER_ADMIN') {
        const count = data.filter(s => s.submittedBy === user.id).length;
        if (count > 0 || user.role === 'REPRESENTATIVE') {
            acc.push({ name: user.name, count, role: user.role });
        }
    }
    return acc;
  }, [] as {name: string, count: number, role: string}[]).sort((a, b) => b.count - a.count);

  const filteredData = data.filter(d => {
    const headName = d.sectionA?.headName || '';
    const houseName = d.sectionA?.houseName || '';
    const wardNo = d.sectionA?.wardNo || '';
    const term = search.toLowerCase();

    const matchesSearch = headName.toLowerCase().includes(term) || houseName.toLowerCase().includes(term);
    const matchesWard = filterWard === '' || wardNo.includes(filterWard);
    
    return matchesSearch && matchesWard;
  });

  const exportCSV = async () => {
    setIsExporting(true);
    try {
      // 1. Prepare Sections
      const sortedSections = [...sections].sort((a,b) => a.order - b.order);
      const visibleSections = sortedSections.filter(s => isSectionVisible(s.id));
      
      const singleSections = visibleSections.filter(s => !s.isRepeater);
      const repeaterSections = visibleSections.filter(s => s.isRepeater);

      // 2. Helper to get questions
      const getQs = (secId: string) => questions.filter(q => q.section === secId);

      // 3. Helper to escape CSV
      const escapeCsv = (str: string) => {
          if (str === undefined || str === null) return '';
          const s = String(str);
          if (s.includes(',') || s.includes('"') || s.includes('\n')) {
              return `"${s.replace(/"/g, '""')}"`;
          }
          return s;
      };

      // 4. Build Headers
      const metaHeaders = ['Survey ID', 'Submitted By', 'Submitted At'];
      let dynamicHeaders: string[] = [];

      singleSections.forEach(s => {
          getQs(s.id).forEach(q => {
              dynamicHeaders.push(`(${s.label}) ${q.label}`);
          });
      });

      // Handle PRIMARY repeater only for flat CSV (usually Family Members)
      const primaryRepeater = repeaterSections.length > 0 ? repeaterSections[0] : null;
      if (primaryRepeater) {
           getQs(primaryRepeater.id).forEach(q => {
              dynamicHeaders.push(`(${primaryRepeater.label}) ${q.label}`);
          });
      }

      const csvHeaders = [...metaHeaders, ...dynamicHeaders].map(escapeCsv);
      const rows: string[][] = [];

      // 5. Build Rows
      filteredData.forEach(survey => {
        const repName = users.find(u => u.id === survey.submittedBy)?.name || 'Unknown';
        const dateStr = survey.submittedAt ? new Date(survey.submittedAt).toLocaleString() : '';
        
        // Base Data (Meta + Single Sections)
        const baseData = [
            escapeCsv(survey.id),
            escapeCsv(repName),
            escapeCsv(dateStr)
        ];

        singleSections.forEach(s => {
            const secData = survey[s.id] || {};
            getQs(s.id).forEach(q => {
                let val = secData[q.id];
                
                // Formatting
                if (val === true) val = 'Yes';
                if (val === false) val = 'No';
                
                let str = val !== undefined && val !== null ? String(val) : '';
                
                if (str.startsWith('data:image')) str = 'Image Saved';
                if (Array.isArray(val)) str = val.join('; ');
                
                baseData.push(escapeCsv(str));
            });
        });

        // Expand Repeater
        if (primaryRepeater) {
             const repeaterItems = survey[primaryRepeater.id];
             if (Array.isArray(repeaterItems) && repeaterItems.length > 0) {
                 repeaterItems.forEach(item => {
                     const itemRow = [...baseData];
                     getQs(primaryRepeater.id).forEach(q => {
                         let val = item[q.id];
                         
                         // Formatting
                         if (val === true) val = 'Yes';
                         if (val === false) val = 'No';

                         let str = val !== undefined && val !== null ? String(val) : '';
                         if (Array.isArray(val)) str = val.join('; ');

                         itemRow.push(escapeCsv(str));
                     });
                     rows.push(itemRow);
                 });
             } else {
                 // Empty row for this survey
                 const emptyCols = getQs(primaryRepeater.id).map(() => '');
                 rows.push([...baseData, ...emptyCols]);
             }
        } else {
             rows.push(baseData);
        }
      });

      const csvContent = "data:text/csv;charset=utf-8," 
        + "\uFEFF" 
        + [csvHeaders.join(','), ...rows.map(e => e.join(','))].join('\n');

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `mahallu_survey_data_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addToast("Export complete!", "success");

    } catch (e) {
      console.error("Export failed", e);
      addToast("Failed to export data", "error");
    } finally {
      setIsExporting(false);
    }
  };

  const downloadAllPhotos = async () => {
    if (filteredData.length === 0) {
        addToast("No surveys found in current list to download.", "info");
        return;
    }

    setIsZipping(true);
    try {
        const zip = new JSZip();
        let count = 0;
        
        // Use a loop to handle async if needed later, but JSZip is sync for adding files
        for (const survey of filteredData) {
            const photos = getAllPhotosFromSurvey(survey);
            if (photos.length > 0) {
                const headName = survey.sectionA?.headName || 'Unknown';
                const serial = survey.sectionA?.serialNo || '0000';
                
                photos.forEach((photoData, idx) => {
                    const parts = photoData.split(',');
                    if (parts.length === 2) {
                        const base64Data = parts[1];
                        // Sanitize filename
                        const safeName = headName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 20);
                        const filename = `${serial}_${safeName}_${idx + 1}.jpg`;
                        
                        try {
                           zip.file(filename, base64Data, {base64: true});
                           count++;
                        } catch(err) {
                           console.error("Failed to add file to zip", filename, err);
                        }
                    }
                });
            }
        }

        if (count === 0) {
            addToast("No valid photos found in the selected surveys.", "info");
            return; // Exit early
        }

        const content = await zip.generateAsync({type: "blob"});
        const url = window.URL.createObjectURL(content);
        const link = document.createElement('a');
        link.href = url;
        link.download = `photos_batch_${new Date().toISOString().slice(0,10)}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        addToast(`Downloaded ${count} photos!`, "success");

    } catch (e: any) {
        console.error("Zip Error:", e);
        addToast("Failed to zip photos: " + (e.message || "Unknown error"), "error");
    } finally {
        setIsZipping(false);
    }
  };

  const openPhotoViewer = (photos: string[]) => {
      if (!photos || photos.length === 0) {
        addToast("No photos to view", "info");
        return;
      }
      setViewPhotos(photos);
      setViewPhotoIndex(0);
  };

  const downloadSinglePhoto = (base64: string, name: string) => {
     const link = document.createElement('a');
     link.href = base64;
     link.download = `photo_${name.replace(/[^a-zA-Z0-9]/g, '_')}.jpg`;
     link.click();
  };

  const initiateDelete = (id: string) => {
    setSurveyToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (surveyToDelete) {
      await deleteSurvey(surveyToDelete);
      addToast("Survey deleted successfully", "success");
      await loadData();
    }
  };

  if (editingSurvey) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="flex items-center mb-4">
            <button onClick={() => setEditingSurvey(null)} className="mr-4 px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-gray-700 font-bold">
                &larr; Back to Dashboard
            </button>
            <h2 className="text-xl font-bold text-gray-800">Editing Survey: {editingSurvey.sectionA?.headName}</h2>
        </div>
        <SurveyForm user={user} onSuccess={handleEditSuccess} initialData={editingSurvey} />
      </div>
    );
  }

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><Loader className="animate-spin text-emerald-600" size={32} /></div>
  }

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Admin Dashboard</h2>
        <div className="flex space-x-2 mt-4 md:mt-0">
            <button 
            onClick={downloadAllPhotos} 
            disabled={isZipping}
            className="bg-purple-600 text-white px-4 py-2 rounded flex items-center space-x-2 hover:bg-purple-700 shadow-md disabled:opacity-70 disabled:cursor-wait"
            >
            {isZipping ? <Loader className="animate-spin" size={18} /> : <ImageIcon size={18} />}
            <span>{isZipping ? 'Zipping...' : 'Download All Photos (ZIP)'}</span>
            </button>
            <button 
            onClick={exportCSV} 
            disabled={isExporting}
            className="bg-blue-600 text-white px-4 py-2 rounded flex items-center space-x-2 hover:bg-blue-700 shadow-md disabled:opacity-70 disabled:cursor-wait"
            >
            {isExporting ? <Loader className="animate-spin" size={18} /> : <Download size={18} />}
            <span>{isExporting ? 'Exporting...' : 'Export CSV'}</span>
            </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-emerald-100 flex items-center space-x-4">
            <div className="bg-emerald-100 p-3 rounded-full text-emerald-600">
                <CheckCircle size={32} />
            </div>
            <div>
                <p className="text-gray-500 text-sm">Total Surveys Filled</p>
                <h3 className="text-3xl font-bold text-gray-800">{totalFilled}</h3>
            </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-purple-100 flex items-center space-x-4">
            <div className="bg-purple-100 p-3 rounded-full text-purple-600">
                <Users size={32} />
            </div>
            <div>
                <p className="text-gray-500 text-sm">Active Representatives</p>
                <h3 className="text-3xl font-bold text-gray-800">{repStats.filter(r => r.count > 0).length}</h3>
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Data Table */}
        <div className="lg:col-span-2 space-y-4">
            <h3 className="font-bold text-gray-700 text-lg">Survey Entries</h3>
            <div className="flex flex-col sm:flex-row gap-4 bg-white p-4 rounded-lg shadow-sm">
                <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input 
                    type="text" 
                    placeholder="Search by Name or House..." 
                    className="pl-10 p-2 border rounded w-full outline-none focus:ring-2 focus:ring-emerald-500"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                </div>
                <div className="relative w-full sm:w-48">
                <Filter className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input 
                    type="text" 
                    placeholder="Filter by Ward..." 
                    className="pl-10 p-2 border rounded w-full outline-none focus:ring-2 focus:ring-emerald-500"
                    value={filterWard}
                    onChange={(e) => setFilterWard(e.target.value)}
                />
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sl No.</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Head Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">House Details</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone No.</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Photos</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.map((survey) => {
                        // Dynamically retrieve all photos from the survey object
                        const photos = getAllPhotosFromSurvey(survey);
                        const submittedByName = users.find(u => u.id === survey.submittedBy)?.name || 'Unknown';
                        const submittedDate = survey.submittedAt ? new Date(survey.submittedAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : 'N/A';
                        
                        return (
                        <tr key={survey.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{survey.sectionA?.serialNo || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{survey.sectionA?.headName || <span className="text-gray-400 italic">(No Name)</span>}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex flex-col">
                                <span>{survey.sectionA?.houseName || '-'}</span>
                                <span className="text-xs text-gray-400">Ward: {survey.sectionA?.wardNo}, No: {survey.sectionA?.houseNo}</span>
                            </div>
                            </td>
                            {/* Robust Phone Display */}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 font-mono">
                                {survey.sectionA?.phone || survey.phone || <span className="text-gray-300">-</span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                <div className="flex items-center space-x-1">
                                    <UserIcon size={14} className="text-emerald-500" />
                                    <span>{submittedByName}</span>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div className="flex items-center space-x-1">
                                    <Calendar size={14} className="text-blue-400" />
                                    <span>{submittedDate}</span>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {photos.length > 0 ? (
                                <div className="flex space-x-2 items-center">
                                    <button onClick={() => openPhotoViewer(photos)} className="text-blue-600 hover:text-blue-800 underline text-xs flex items-center font-bold bg-blue-50 px-2 py-1 rounded">
                                        <Eye size={12} className="mr-1"/>
                                        View ({photos.length})
                                    </button>
                                </div>
                            ) : <span className="text-gray-300 text-xs">No Photos</span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex items-center space-x-2">
                                {(user.role === 'MASTER_ADMIN' || user.role === 'ADMIN') && (
                                <button 
                                    onClick={() => setEditingSurvey(survey)}
                                    className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-2 rounded transition-colors"
                                    title="Edit Survey"
                                >
                                    <Edit size={18} />
                                </button>
                                )}
                                {(user.role === 'MASTER_ADMIN') && (
                                <button 
                                    onClick={() => initiateDelete(survey.id)}
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded transition-colors"
                                    title="Delete Survey"
                                >
                                    <Trash2 size={18} />
                                </button>
                                )}
                            </td>
                        </tr>
                    )})}
                    {filteredData.length === 0 && (
                    <tr>
                        <td colSpan={8} className="px-6 py-8 text-center text-gray-500 italic">No records found matching your filters.</td>
                    </tr>
                    )}
                </tbody>
                </table>
            </div>
        </div>

        <div className="space-y-4">
            <h3 className="font-bold text-gray-700 text-lg flex items-center"><Trophy className="mr-2 text-yellow-500" size={20}/> Performance</h3>
            <div className="bg-white rounded-lg shadow p-4">
                <ul className="space-y-3">
                    {repStats.map((rep, idx) => (
                        <li key={idx} className="flex justify-between items-center border-b border-gray-100 last:border-0 pb-2 last:pb-0">
                            <div>
                                <p className="font-bold text-gray-800 text-sm">{rep.name}</p>
                                <p className="text-xs text-gray-400 capitalize">{rep.role.toLowerCase()}</p>
                            </div>
                            <div className="bg-emerald-100 text-emerald-700 font-bold px-3 py-1 rounded-full text-xs">
                                {rep.count} Forms
                            </div>
                        </li>
                    ))}
                    {repStats.length === 0 && <p className="text-gray-400 text-sm text-center">No representative data.</p>}
                </ul>
            </div>
        </div>
      </div>

      <SecureActionModal 
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Survey Response"
        message="Are you sure you want to delete this survey response? This action cannot be undone."
      />

      {/* Photo View Modal with Gallery Support */}
      {viewPhotos.length > 0 && (
         <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex flex-col items-center justify-center p-4">
            <button className="absolute top-4 right-4 text-white hover:text-red-400 p-2 z-50" onClick={() => setViewPhotos([])}>
                <X size={32} className="bg-gray-800 rounded-full p-1"/>
            </button>
            
            <div className="relative max-w-4xl w-full h-full flex flex-col items-center justify-center">
               <div className="relative">
                   <img src={viewPhotos[viewPhotoIndex]} className="max-w-full max-h-[80vh] rounded shadow-2xl object-contain border-4 border-gray-800" alt="House Proof" />
                   <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                       {viewPhotoIndex + 1} / {viewPhotos.length}
                   </div>
               </div>
               
               <div className="mt-6 flex items-center space-x-6 bg-gray-900 p-4 rounded-xl border border-gray-700 shadow-xl">
                   {viewPhotos.length > 1 && (
                     <div className="flex space-x-4">
                        <button 
                            onClick={() => setViewPhotoIndex((prev) => (prev - 1 + viewPhotos.length) % viewPhotos.length)}
                            className="text-white px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-bold"
                        >
                            &larr; Prev
                        </button>
                        <button 
                            onClick={() => setViewPhotoIndex((prev) => (prev + 1) % viewPhotos.length)}
                            className="text-white px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-bold"
                        >
                            Next &rarr;
                        </button>
                     </div>
                   )}
                   <div className="h-8 w-px bg-gray-600 mx-2"></div>
                   <button onClick={() => downloadSinglePhoto(viewPhotos[viewPhotoIndex], `photo_${viewPhotoIndex+1}`)} className="text-emerald-400 flex items-center text-sm font-bold hover:text-emerald-300 hover:bg-gray-800 px-3 py-2 rounded transition-colors">
                       <Download size={18} className="mr-2"/> Download
                   </button>
               </div>
            </div>
         </div>
      )}
    </div>
  );
};

export default Dashboard;